/**
 * contract-helpers.ts — Kiểu dữ liệu + hàm thuần cho Platform → Hợp đồng (ký tay online) và trang ký công khai.
 * Backend: ecdp_backend_source/src/modules/platform/contract/* (server kiểm lại mọi thứ — ở đây chỉ kiểm sớm để báo lỗi nhanh).
 */

export type ContractStatus = 'draft' | 'sent' | 'signed' | 'declined' | 'expired' | 'cancelled'
export const STATUS_LABELS: Record<ContractStatus, string> = {
  draft: 'Nháp', sent: 'Đã gửi — chờ ký', signed: 'Đã ký', declined: 'Khách từ chối', expired: 'Quá hạn ký', cancelled: 'Đã huỷ',
}
export const STATUS_VARIANT: Record<ContractStatus, 'secondary' | 'default' | 'success' | 'outline' | 'warning' | 'destructive'> = {
  draft: 'secondary', sent: 'default', signed: 'success', declined: 'destructive', expired: 'warning', cancelled: 'outline',
}

export interface Clause { title: string; text: string }
export interface ContractCustomer { fullName: string; idNumber: string; address: string; email: string; phone: string }
export interface ContractDeal { description: string; amount: number | null }

export interface ContractListItem {
  id: string
  number: string
  customerName: string
  customerEmail: string
  amount: number | null
  description: string
  status: ContractStatus
  displayStatus: ContractStatus
  expiresAt: string | null
  sentAt: string | null
  signedAt: string | null
  createdAt: string
}

export interface ContractEvent {
  id: string
  type: string
  actor: string
  ip: string | null
  userAgent: string | null
  meta: Record<string, unknown>
  at: string
}

export interface Contract {
  id: string
  number: string
  orgId: string | null
  quoteId: string | null
  customer: ContractCustomer
  deal: ContractDeal
  seller: { companyName?: string; signerName?: string; signerTitle?: string }
  hasSellerSignature: boolean
  body: Clause[]
  bodyHash: string | null
  status: ContractStatus
  displayStatus: ContractStatus
  expiresAt: string | null
  sentAt: string | null
  note: string
  signerName: string | null
  signedAt: string | null
  signedIp: string | null
  signedUserAgent: string | null
  declinedAt: string | null
  declineReason: string | null
  cancelledAt: string | null
  cancelReason: string | null
  hasPdf: boolean
  pdfHash: string | null
  pdfBytes: number | null
  createdByName?: string | null
  createdAt: string
  events?: ContractEvent[]
}

export interface ContractTemplate {
  companyName: string
  taxCode: string
  address: string
  phone: string
  email: string
  signerName: string
  signerTitle: string
  hasSignature: boolean
  expiresDays: number
  clauses: Clause[]
}

export interface TemplateResponse {
  template: ContractTemplate
  signaturePreview: string | null
  variables: Array<{ key: string; label: string }>
  updatedAt: string | null
}

export const EVENT_LABELS: Record<string, string> = {
  created: 'Lập hợp đồng', updated: 'Sửa hợp đồng', sent: 'Gửi link ký cho khách', email_failed: 'Gửi email lỗi',
  viewed: 'Khách mở link', otp_requested: 'Khách xin mã OTP', otp_throttled: 'Xin mã OTP bị chặn (quá nhanh / quá nhiều)',
  otp_failed: 'Nhập sai mã OTP', otp_locked: 'Khoá nhập OTP (sai quá 5 lần)', sign_pdf_failed: 'Dựng PDF lỗi — chưa ký',
  integrity_failed: 'Nội dung không khớp mã băm — chặn ký', signed: 'Khách đã ký', signed_email: 'Gửi bản đã ký cho khách',
  declined: 'Khách từ chối', cancelled: 'Huỷ hợp đồng', downloaded: 'Tải PDF đã ký',
}
/** Sự kiện cần chú ý (nghi ngờ / lỗi) — tô màu cảnh báo trong nhật ký. */
export const EVENT_WARN = new Set(['email_failed', 'otp_throttled', 'otp_failed', 'otp_locked', 'sign_pdf_failed', 'integrity_failed'])

// ── Định dạng ────────────────────────────────────────────────────────

export const fmtMoney = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${new Intl.NumberFormat('vi-VN').format(n)} ₫`)

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Còn bao lâu tới hạn: "còn 3 ngày", "còn 5 giờ", "đã quá hạn". */
export function timeLeft(iso: string | null, now: Date = new Date()): string {
  if (!iso) return ''
  const ms = new Date(iso).getTime() - now.getTime()
  if (Number.isNaN(ms)) return ''
  if (ms <= 0) return 'đã quá hạn'
  const hours = Math.floor(ms / 3_600_000)
  if (hours >= 24) return `còn ${Math.floor(hours / 24)} ngày`
  if (hours >= 1) return `còn ${hours} giờ`
  return `còn ${Math.max(1, Math.floor(ms / 60_000))} phút`
}

// ── Form ─────────────────────────────────────────────────────────────

export interface ContractForm {
  fullName: string
  idNumber: string
  address: string
  email: string
  phone: string
  description: string
  amount: string
  note: string
  orgId: string | null
  quoteId: string | null
  /** null = dùng điều khoản của Mẫu hợp đồng. */
  clauses: Clause[] | null
}

export const emptyForm = (): ContractForm => ({
  fullName: '', idNumber: '', address: '', email: '', phone: '', description: '', amount: '', note: '', orgId: null, quoteId: null, clauses: null,
})

export function contractToForm(c: Contract): ContractForm {
  return {
    fullName: c.customer.fullName, idNumber: c.customer.idNumber, address: c.customer.address, email: c.customer.email,
    phone: c.customer.phone, description: c.deal.description, amount: c.deal.amount === null ? '' : String(c.deal.amount),
    note: c.note, orgId: c.orgId, quoteId: c.quoteId, clauses: c.body,
  }
}

const EMAIL_RE = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']{2,}$/

/** Lỗi nhập (tiếng Việt) hoặc null. */
export function validateForm(f: ContractForm): string | null {
  if (f.fullName.trim().length < 2) return 'Nhập họ tên khách'
  if (!EMAIL_RE.test(f.email.trim())) return 'Email khách không hợp lệ (mã OTP sẽ gửi về email này)'
  if (f.idNumber.trim() && !/^[0-9A-Za-z]{6,20}$/.test(f.idNumber.trim())) return 'Số CCCD/CMND chỉ gồm chữ và số (6–20 ký tự)'
  if (f.phone.trim() && !/^[0-9+() .-]{8,20}$/.test(f.phone.trim())) return 'SĐT khách không hợp lệ'
  if (f.amount.trim() && !/^\d{1,13}$/.test(f.amount.trim())) return 'Số tiền phải là số nguyên đồng'
  if (f.clauses) {
    if (f.clauses.length === 0) return 'Cần ít nhất 1 điều khoản'
    const bad = f.clauses.findIndex((c) => !c.title.trim() || !c.text.trim())
    if (bad >= 0) return `Điều khoản ${bad + 1}: nhập đủ tiêu đề và nội dung`
  }
  return null
}

export function formToInput(f: ContractForm) {
  return {
    customer: { fullName: f.fullName.trim(), idNumber: f.idNumber.trim(), address: f.address.trim(), email: f.email.trim(), phone: f.phone.trim() },
    deal: { description: f.description.trim(), amount: f.amount.trim() === '' ? null : Number(f.amount.trim()) },
    clauses: f.clauses,
    note: f.note.trim(),
    orgId: f.orgId,
    quoteId: f.quoteId,
  }
}

/** Chèn `{{biến}}` vào cuối nội dung điều khoản. */
export const insertVariable = (text: string, key: string) => `${text}${text && !/\s$/.test(text) ? ' ' : ''}{{${key}}}`

export function moveClause(list: Clause[], from: number, to: number): Clause[] {
  if (to < 0 || to >= list.length || from === to) return list
  const next = [...list]
  const [it] = next.splice(from, 1)
  next.splice(to, 0, it)
  return next
}

// ── Trang ký công khai ───────────────────────────────────────────────

export interface PublicContract {
  number: string
  status: ContractStatus
  statusLabel: string
  seller: { companyName: string; signerName: string; signerTitle: string }
  customer: { fullName: string; emailMasked: string }
  clauses: Clause[]
  expiresAt: string | null
  signedAt: string | null
  canSign: boolean
  hasSellerSignature: boolean
}

/** Mã OTP: chỉ giữ chữ số, tối đa 6 (dán "123 456" vẫn nhận). */
export const normalizeOtp = (s: string) => s.replace(/\D/g, '').slice(0, 6)

export interface SignCheck { name: string; otp: string; hasStroke: boolean; agree: boolean; otpSent: boolean }
/** Lỗi trước khi bấm Ký, hoặc null. */
export function signBlocker(c: SignCheck): string | null {
  if (c.name.trim().length < 2) return 'Nhập họ tên đầy đủ'
  if (!c.hasStroke) return 'Hãy vẽ chữ ký vào khung'
  if (!c.otpSent) return 'Bấm "Gửi mã xác thực" để nhận mã qua email'
  if (c.otp.length !== 6) return 'Nhập mã xác thực gồm 6 chữ số'
  if (!c.agree) return 'Tick đồng ý với nội dung hợp đồng'
  return null
}

/** Đếm ngược cho nút "Gửi lại mã": giây còn lại (≥0). */
export const cooldownLeft = (sentAtMs: number | null, nowMs: number, cooldownSec = 60) =>
  sentAtMs === null ? 0 : Math.max(0, Math.ceil((sentAtMs + cooldownSec * 1000 - nowMs) / 1000))

/** Từ <canvas> → có nét vẽ không (đủ số điểm ảnh không trong suốt). Nhận ImageData để test được không cần DOM. */
export function hasInk(data: Uint8ClampedArray, minPixels = 40): boolean {
  let n = 0
  for (let i = 3; i < data.length; i += 4) if (data[i] > 20 && ++n >= minPixels) return true
  return false
}
